import { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import ArrowIcon from "components/CalendarView/ArrowIcon";
import moment from "moment";
import styled from "styled-components";
import CalendarDay from "components/CalendarView/CalendarDay";
import { createStructuredSelector } from "reselect";
import {
  makeSelectActiveStartDate,
  makeSelectCurrentMonth,
  makeSelectPlannedTimeSlots,
  makeSelectPracticeProductOrders,
  makeSelectTreatments,
} from "containers/App/selectors";
import {
  getActiveStartDate,
  getPlannedTimeSlotsAction,
  getPracticeProductOrders,
  setDate,
} from "containers/App/actions";
import { connect } from "react-redux";
import { compose } from "redux";
import { DATE_FORMATS } from "utils/Date";

const useStyles = makeStyles(theme => ({
  wrapper: {
    minWidth: "100px",
    overflow: "hidden",
    borderRadius: "10px",
    border: "1px solid #454546",
  },
  header: {
    backgroundColor: "#454546",
    color: "white",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0.5, 2),
  },
  arrowWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    outline: "none",
  },
  dayHeader: {
    padding: theme.spacing(0.5, 1),
    justifySelf: "center",
    alignSelf: "center",
  },
  day: {
    justifySelf: "center",
    alignSelf: "center",
    width: "100%",
    height: "100%",
    textAlign: "center",
  },
}));

const DAY_MAPPING = new Map([
  [0, "sunday"],
  [1, "monday"],
  [2, "tuesday"],
  [3, "wednesday"],
  [4, "thursday"],
  [5, "friday"],
  [6, "saturday"],
]);

function CalendarView({
  currentMonth,
  onClick,
  getPlannedTimeSlots,
  onGetPracticeProductOrders,
  plannedTimeSlots,
  treatments,
  productOrders,
  selectedDay = null,
  onSetDate,
}) {
  useEffect(
    () => {
      getPlannedTimeSlots();
      onGetPracticeProductOrders();
    },
    [getPlannedTimeSlots, onGetPracticeProductOrders],
  );

  const now = useMemo(() => moment(), []);
  const [currentDate, setCurrentDate] = useState(
    now
      .clone()
      .month(currentMonth?.month() || now.month())
      .year(currentMonth?.year() || now.year()),
  );
  const [activeDay, setActiveDay] = useState(null);

  // Callbacks
  const next = useCallback(
    () => {
      setCurrentDate(current => {
        const newDate = current.clone().add(1, "month");
        onSetDate(newDate);
        return newDate;
      });
    },
    [setCurrentDate, onSetDate],
  );

  const previous = useCallback(
    () => {
      setCurrentDate(current => {
        const newDate = current.clone().subtract(1, "month");
        onSetDate(newDate);
        return newDate;
      });
    },
    [setCurrentDate, onSetDate],
  );

  const handleSetDate = useCallback(
    date => {
      setCurrentDate(current => {
        const newDate = current
          .clone()
          .month(date.month())
          .year(date.year());
        onSetDate(newDate);
        return newDate;
      });
    },
    [setCurrentDate, onSetDate],
  );

  useEffect(
    () => {
      if (selectedDay) {
        const selectDay = moment(selectedDay, DATE_FORMATS.DATE);
        setActiveDay(selectDay);

        setCurrentDate(current => {
          if (
            selectDay.month() !== current.month() ||
            selectDay.year() !== current.year()
          ) {
            handleSetDate(selectDay);
          }

          return current;
        });
      }
    },
    [selectedDay, setCurrentDate, handleSetDate],
  );

  // Memo variables
  const displayDate = useMemo(() => currentDate.format("MMMM YYYY"), [
    currentDate,
  ]);

  const isNextDisabled = useMemo(
    () =>
      currentDate
        .clone()
        .add(1, "month")
        .isAfter(now.clone().add(4, "months"), "M"),
    [now, currentDate],
  );

  const isPreviousDisabled = useMemo(
    () =>
      currentDate
        .clone()
        .subtract(1, "month")
        .isBefore(now.clone(), "M"),
    [currentDate, now],
  );

  const weeksInMonth = useMemo(
    () => {
      const weeks = new Set();
      const firstDayOfMonth = currentDate.clone().date(1);
      const currentDay = firstDayOfMonth.clone();

      while (currentDay.clone().startOf("week").month() === firstDayOfMonth.month() || currentDay.clone().endOf("week").month() === firstDayOfMonth.month()) {
        weeks.add(currentDay.clone());
        currentDay.add(1, "week");
      }

      return weeks;
    },
    [currentDate],
  );

  const days = useMemo(
    () => {
      const dayCollection = [];

      for (const week of weeksInMonth) {
        const currentWeek = moment(week, "w");

        // push days
        for (let i = 1; i < 8; i++) {
          dayCollection.push(currentWeek.clone().isoWeekday(i));
        }
      }

      return dayCollection;
    },
    [weeksInMonth],
  );

  const classes = useStyles();

  const dayRendering = useMemo(
    () => (
      <CalendarWrapper rows={weeksInMonth}>
        <span className={classes.dayHeader} style={{ gridArea: "monday" }}>
          MA
        </span>
        <span className={classes.dayHeader} style={{ gridArea: "tuesday" }}>
          DI
        </span>
        <span className={classes.dayHeader} style={{ gridArea: "wednesday" }}>
          WO
        </span>
        <span className={classes.dayHeader} style={{ gridArea: "thursday" }}>
          DO
        </span>
        <span className={classes.dayHeader} style={{ gridArea: "friday" }}>
          VR
        </span>
        <span className={classes.dayHeader} style={{ gridArea: "saturday" }}>
          ZA
        </span>
        <span className={classes.dayHeader} style={{ gridArea: "sunday" }}>
          ZO
        </span>
        {days.map(d => (
          <span
            key={d.dayOfYear()}
            className={classes.day}
            style={{ gridArea: `${DAY_MAPPING.get(d.day())}${d.isoWeek()}` }}
          >
            <CalendarDay
              onClick={onClick}
              treatments={treatments}
              orders={productOrders}
              activeDay={activeDay}
              timeslots={plannedTimeSlots}
              currentMonth={currentDate.month()}
              now={now}
              day={d}
            />
          </span>
        ))}
      </CalendarWrapper>
    ),
    [
      classes.day,
      now,
      days,
      weeksInMonth,
      currentDate,
      classes.dayHeader,
      onClick,
      treatments,
      productOrders,
      plannedTimeSlots,
      activeDay,
    ],
  );

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <button
          type="button"
          onClick={previous}
          disabled={isPreviousDisabled}
          className={classes.arrowWrapper}
        >
          <ArrowIcon direction="left" disabled={isPreviousDisabled} />
        </button>
        <span>{displayDate}</span>
        <button
          type="button"
          onClick={next}
          className={classes.arrowWrapper}
          disabled={isNextDisabled}
        >
          <ArrowIcon direction="right" disabled={isNextDisabled} />
        </button>
      </div>
      {dayRendering}
    </div>
  );
}

const CalendarWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: ${({ rows = [] }) => `repeat(${rows.size}, 1fr)`};
  grid-template-areas: ${({ rows = [] }) => {
    const areas = [
      '"monday tuesday wednesday thursday friday saturday sunday"',
    ];

    for (const week of rows) {
      const isoWeek = week.isoWeek();
      areas.push(
        `"monday${isoWeek} tuesday${isoWeek} wednesday${isoWeek} thursday${isoWeek} friday${isoWeek} saturday${isoWeek} sunday${isoWeek}"`,
      );
    }

    return areas.join(" ");
  }};
`;

CalendarView.propTypes = {
  onClick: PropTypes.func.isRequired,
  currentMonth: PropTypes.instanceOf(moment),
  plannedTimeSlots: PropTypes.array,
  getPlannedTimeSlots: PropTypes.func.isRequired,
  onGetPracticeProductOrders: PropTypes.func.isRequired,
  treatments: PropTypes.array,
  productOrders: PropTypes.array,
  selectedDay: PropTypes.string,
  onSetDate: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  plannedTimeSlots: makeSelectPlannedTimeSlots(),
  treatments: makeSelectTreatments(),
  productOrders: makeSelectPracticeProductOrders(),
  activeStartDate: makeSelectActiveStartDate(),
  currentMonth: makeSelectCurrentMonth(),
});

function mapDispatchToProps(dispatch) {
  return {
    getPlannedTimeSlots: () => dispatch(getPlannedTimeSlotsAction()),
    onGetPracticeProductOrders: () => dispatch(getPracticeProductOrders()),
    onGetActiveStartDate: activeStartDate =>
      dispatch(getActiveStartDate(activeStartDate)),
    onSetDate: date => dispatch(setDate(date)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(withConnect)(CalendarView);
